import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import moment from 'moment';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import Notify from '../../../../../shared/components/widgets/Notify';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';

export default function DspContextualTargets() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [forceRefresh, setForceRefresh] = useState(moment());
  const [agGrid, setAgGrid] = useState(null);
  const [updatedRowCount, setUpdatedRowCount] = useState(0);
  const [originalData, setOriginalData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // function jsonCellRenderer(params) {
  // // Hold  cellRenderer: (params) => <pre style={{ fontFamily: 'monospace', margin: 0, padding: 0, whiteSpace: 'pre-wrap' }}>${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>
  //   return `<pre style="font-family: monospace; margin: 0; padding: 0; white-space: pre-wrap;">${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>`;
  // }

  const resetRow = (orgRowObj) => {
    agGrid.gridApi.forEachNode((rowNode) => {
      if (orgRowObj.contextualTargetId === rowNode.data.contextualTargetId) {
        rowNode.setData(orgRowObj);
        setUpdatedRowCount(curentCount => curentCount - 1);
      }
    });
  };

  const removeRow = (rowData) => {
    agGrid.gridApi.applyTransaction({ remove: [rowData.data] });
    setUpdatedRowCount(curentCount => curentCount - 1);
  };

  const submitDspCT = () => {
    setSubmitting(true);
    const dspContextualTargetsUrl = `${config.serverUrl}/management/dsp/targets/contextual`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const saveData = [];
    // Build changes
    agGrid.gridApi.forEachNode((rowNode) => {
      if (rowNode.data.change || rowNode.data.contextualTargetId === 0) {
        const { change, ...rest } = rowNode.data;
        Object.entries(rest).forEach(([key, value]) => {
          if (key === 'bidMultiplier' && (typeof value !== 'number' || Number.isNaN(value))) {
            rest[key] = parseFloat(value || 0.0);
          }
        });
        saveData.push({ ...rest }); // Remove fields we don't care about and add to changedRows
      }
    });

    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId: selectedSeller.value,
        saveData,
      }),
    };

    fetch(dspContextualTargetsUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then(() => {
        Notify.show('primary', 'Success', 'ContextualTargets have been saved!');
        setUpdatedRowCount(0);
        setForceRefresh(moment());
        setSubmitting(false);
      }).catch(() => {
        Notify.show('danger', 'Error', 'We were unable to save your ContextualTargets.');
        setForceRefresh(moment());
        setSubmitting(false);
      });
  };

  const highlightChanges = (row) => {
    if ((row.data?.change && row.value !== row.data.change[row.colDef.field]) || (row.data.contextualTargetId === 0)) {
      return { backgroundColor: '#91BE23' };
    }
    return { backgroundColor: '' };
  }

  const columnDefs = [
    {
      field: 'change',
      headerName: 'CHANGE',
      cellStyle: (params) => (params.value !== '' ? { color: '#91BE23' } : { color: 'black' }),
      cellRenderer: (params) => {
        if (params.value && params.data.contextualTargetId > 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => resetRow(params.value)}>Reset Row</button>;
        }
        if (params.data.contextualTargetId === 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => removeRow(params)}>Remove Row</button>;
        }
        return ''
      },
      editable: false,
    },
    { field: 'contextualTargetId', headerName: 'Contextual Target ID', editable: false },
    { field: 'accountId', headerName: 'Account ID', editable: false },
    { field: 'customName', headerName: 'Custom Name', cellStyle: params => highlightChanges(params) },
    { field: 'targetType', headerName: 'Target Type', cellStyle: params => highlightChanges(params) },
    { field: 'matchType', headerName: 'Match Type', cellStyle: params => highlightChanges(params) },
    { field: 'targetValueCsv', headerName: 'Target Value CSV', cellStyle: params => highlightChanges(params) },
    { field: 'gender', headerName: 'Gender', cellStyle: params => highlightChanges(params) },
    { field: 'destination', headerName: 'Destination', cellStyle: params => highlightChanges(params) },
    { field: 'landingPageType', headerName: 'Landing Page Type', cellStyle: params => highlightChanges(params) },
    { field: 'bcc', headerName: 'BCC', cellStyle: params => highlightChanges(params) },
    { field: 'bidMultiplier', headerName: 'Bid Multiplier', cellStyle: params => highlightChanges(params) },
    { field: 'createTs', headerName: 'Create TS', editable: false },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: true,
  };

  const showDatePicker = false;
  const showExportOptions = true;
  const apiEndPoint = `${config.serverUrl}/management/dsp/targets/contextual`;

  const exportPrefix = getExportPrefix('amz-dsp-targets-contextual', selectedSeller, true);

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  const parentSetData = (data) => {
    setOriginalData(JSON.parse(JSON.stringify(data)));
  }

  const onRowValueChanged = (cellData) => {
    if (!agGrid) {
      return;
    }
    agGrid.gridApi.forEachNode((rowNode) => {
      if (cellData.data.contextualTargetId === rowNode.data.contextualTargetId) {
        if (!rowNode.data.change) {
          const origData = originalData.find(origRow => origRow.contextualTargetId === cellData.data.contextualTargetId);
          rowNode.setDataValue('change', { ...origData });
          if (cellData.data.contextualTargetId !== 0) {
            setUpdatedRowCount(curentCount => curentCount + 1);
          }
        }
      }
    })
  }

  const getNewRow = () => {
    const newData = {
      contextualTargetId: 0,
      accountId: selectedSeller.value,
      customName: null,
      targetType: null,
      matchType: null,
      targetValueCsv: null,
      gender: null,
      destination: null,
      landingPageType: null,
      bcc: null,
      bidMultiplier: null,
      createTs: null,
    };
    setUpdatedRowCount(curentCount => curentCount + 1);
    return newData;
  }


  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
    }
  }, [forceRefresh]);

  return (
    <Container>
      <Row>
        <TableBuilder
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          apiEndPoint={apiEndPoint}
          showDatePicker={showDatePicker}
          showExportOptions={showExportOptions}
          exportPrefix={exportPrefix}
          parentAccess={parentAccess}
          parentSetData={parentSetData}
          onRowValueChanged={onRowValueChanged}
          showAddRowButton
          getNewRow={getNewRow}
        />
      </Row>
      <Row>
        <CardPanel
          title={`Updated Count: ${updatedRowCount}`}
          autorenewButton={false}
          closeButton={false}
        >
          <div style={{ width: '500px' }}>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitDspCT()}>Save Contextual Targets</button>
          </div>
        </CardPanel>
      </Row>
    </Container>
  );
}
